import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Heading,
  Counter,
  SearchInput,
  Tabs,
  TabList,
  TabItem,
  TabPanel,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { AddButton } from './components/AddButton';
import { useQuery } from 'react-query';
import { BulkActions } from './components/BulkActions';
import { PeopleList } from './PeopleList';
import api from 'api';
import {
  activePeopleApiParamsMap,
  employeeTypePeopleApiParamsMap,
  getPeopleTabs,
} from './components/data';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { PeopleTabValue, VisiblePeopleTabValue } from './types';
import { routePaths } from 'components/Routes/data';
import {
  transformSearchResultsToTableNodes,
  transformEmployeesToTableNodes,
  transformDraftEmployeesToTableNodes,
} from './utils';
import { PeopleSummaryData } from 'api/gql-utils/employeeOnboardingGql/types';
import { DeleteDraftModal } from './components/DeleteDraftModal';
import useReduxSelector from 'utils/useReduxSelector';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import permissions from 'constants/permissions';
import { getContractorText } from 'utils/getContractorText';

export const People = () => {
  const { currentPage, setCurrentPage, itemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });
  const [activeTab, setActiveTab] = useState<PeopleTabValue>('active');
  const [searchValue, setSearchValue] = useState('');
  const { debouncedValue: debouncedSearchValue } = useDebouncedValue(searchValue);
  const [deleteDraftId, setDeleteDraftId] = useState<number>();
  const userPermissions = useReduxSelector(loggedInUserSelectors.permissions);
  const isRxCurrentAccountMerchant = !!useReduxSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const featureFlags = useReduxSelector(loggedInUserSelectors.features);

  const { contractorsTitleCase } = getContractorText({
    isRxCurrentAccountMerchant,
    featureFlags,
  });

  const canAddEmployee = userPermissions.includes(permissions.EMPLOYEE_ADD);

  const peopleSummaryQueryResponse = useQuery({
    queryKey: ['people-summary'],
    queryFn: () => api.employeeOnboardingGql.queries.getPeopleListSummary(),
  });

  const peopleListSummaryData = peopleSummaryQueryResponse.data?.payrollFetchPeopleSummary?.data;

  const history = useHistory();

  const params = {
    type: employeeTypePeopleApiParamsMap[activeTab],
    isActive: activePeopleApiParamsMap[activeTab],
  };

  const peopleQueryResponse = useQuery({
    queryKey: ['people', currentPage, itemsPerPage, params],
    queryFn: () =>
      api.employeeOnboardingGql.queries.getPeopleList({
        page: currentPage + 1,
        pageSize: itemsPerPage,
        ...params,
      }),
    enabled: activeTab !== 'search',
  });

  const peopleQueryResponseData = peopleQueryResponse?.data?.payrollFetchEmployees?.data;

  const draftEmployeesQueryResponse = useQuery({
    queryKey: ['draft-employees', currentPage, itemsPerPage],
    queryFn: () =>
      api.employeeOnboardingGql.queries.getDraftEmployeeList({
        page: currentPage + 1,
        pageSize: itemsPerPage,
      }),
    enabled: activeTab !== 'search',
  });

  const draftEmployeesQueryResponseData =
    draftEmployeesQueryResponse?.data?.payrollFetchDraftEmployees?.data;

  const searchPeopleQueryResponse = useQuery({
    queryKey: ['search-people', debouncedSearchValue],
    queryFn: () => api.search.searchEmployees(debouncedSearchValue),
    enabled: activeTab === 'search' && !!debouncedSearchValue,
  });

  const handleClearSearch = () => {
    setActiveTab('active');
    setSearchValue('');
    setCurrentPage(0);
  };

  const handleSearchChange = ({ value }: { value?: string }) => {
    if (!value) {
      handleClearSearch();
    } else {
      if (currentPage > 0) {
        setCurrentPage(0);
      }
      if (activeTab) {
        setActiveTab('search');
      }
      setSearchValue(value || '');
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value as VisiblePeopleTabValue);
    setCurrentPage(0);
    setSearchValue('');
  };

  const handleResumeOnboarding = (employeeId: number) => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.basicDetails,
      search: `?draftId=${employeeId}`,
    });
  };

  const handleDeleteDraftModal = (employeeId: number) => {
    setDeleteDraftId(employeeId);
  };

  return (
    <Box padding={['spacing.7', 'spacing.8', 'spacing.8', 'spacing.8']}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="spacing.4">
        <Heading size="large" color="surface.text.gray.normal">
          People
        </Heading>

        <Box display="flex" alignItems="center" gap="spacing.4">
          <Box width="320px">
            <SearchInput
              label=""
              value={searchValue}
              placeholder="Search by name, email or employee ID"
              onClearButtonClick={handleClearSearch}
              onChange={handleSearchChange}
            />
          </Box>

          {canAddEmployee && (
            <>
              <AddButton />
              <BulkActions />
            </>
          )}
        </Box>
      </Box>

      <Box marginY="spacing.7">
        <Tabs
          value={activeTab}
          // added key here so that it deselects the selected tab when selected tab value set to a "search"(which is not rendered in the tabs)
          key={(activeTab === 'search').toString()}
          onChange={handleTabChange}
          variant="bordered"
          size="medium"
          isLazy>
          <TabList>
            {getPeopleTabs(contractorsTitleCase).map((tab) => (
              <TabItem
                key={tab.value}
                isDisabled={peopleQueryResponse.isLoading || peopleSummaryQueryResponse.isLoading}
                value={tab.value}
                trailing={
                  <Counter
                    value={peopleListSummaryData?.[tab.key as keyof PeopleSummaryData] || 0}
                    color="neutral"
                  />
                }>
                {tab.label}
              </TabItem>
            ))}
          </TabList>
          <Box marginTop="spacing.5">
            <TabPanel value="active">
              <PeopleList
                queryResponse={peopleQueryResponse}
                employees={transformEmployeesToTableNodes(peopleQueryResponseData?.users)}
                activeTab={activeTab}
                totalCount={peopleQueryResponseData?.total ?? 0}
              />
            </TabPanel>

            <TabPanel value="employees">
              <PeopleList
                queryResponse={peopleQueryResponse}
                employees={transformEmployeesToTableNodes(peopleQueryResponseData?.users)}
                activeTab={activeTab}
                totalCount={peopleQueryResponseData?.total ?? 0}
              />
            </TabPanel>

            <TabPanel value="dismissed">
              <PeopleList
                queryResponse={peopleQueryResponse}
                employees={transformEmployeesToTableNodes(peopleQueryResponseData?.users)}
                activeTab={activeTab}
                totalCount={peopleQueryResponseData?.total ?? 0}
              />
            </TabPanel>

            <TabPanel value="contractors">
              <PeopleList
                queryResponse={peopleQueryResponse}
                employees={transformEmployeesToTableNodes(peopleQueryResponseData?.users)}
                activeTab={activeTab}
                totalCount={peopleQueryResponseData?.total ?? 0}
              />
            </TabPanel>

            <TabPanel value="drafts">
              <PeopleList
                queryResponse={draftEmployeesQueryResponse}
                employees={transformDraftEmployeesToTableNodes({
                  draftEmployees: draftEmployeesQueryResponseData?.data,
                  onResumeOnboarding: handleResumeOnboarding,
                  onDeleteDraft: handleDeleteDraftModal,
                })}
                activeTab={activeTab}
                totalCount={draftEmployeesQueryResponseData?.total ?? 0}
              />
            </TabPanel>

            <TabPanel value="search">
              <PeopleList
                queryResponse={searchPeopleQueryResponse}
                employees={transformSearchResultsToTableNodes(searchPeopleQueryResponse?.data)}
                activeTab={activeTab}
                paginationType="client"
                totalCount={searchPeopleQueryResponse?.data?.length ?? 0}
              />
            </TabPanel>
          </Box>
        </Tabs>
      </Box>
      <DeleteDraftModal
        deleteDraftId={deleteDraftId}
        onDismiss={() => setDeleteDraftId(undefined)}
      />
    </Box>
  );
};
